import React from "react";

import {
  ServiceAccount,
  ServiceAccountKey,
} from "../../application/domain/service-account/service-account";
import { reportUnexpectedError } from "../../error-reporter";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "../../graphql/client";

type createServiceAccountKeyFunctionType = (
  param: { expiredAtUnixMilliSecond: number; name: string },
  callback: {
    onSucceeded: (newKey: ServiceAccountKey, privateKeyPem: string) => void;
    onFailed: (errorMessage: string) => void;
  },
) => void;

type updateServiceAccountFunctionType = (
  param: Pick<ServiceAccount, "description" | "roles" | "groupsRoles">,
  callback: {
    onSucceeded: (updated: ServiceAccount) => void;
    onFailed: (errorMessage: string) => void;
  },
) => void;

type disableServiceAccountFunctionType = (
  param: { serviceAccountId: ServiceAccount["id"] },
  callback: {
    onSucceeded: () => void;
    onFailed: (errorMessage: string) => void;
  },
) => void;

type disableServiceAccountKeyFunctionType = (
  param: { serviceAccountKeyId: ServiceAccountKey["id"] },
  callback: {
    onSucceeded: () => void;
    onFailed: (errorMessage: string) => void;
  },
) => void;

type UseServiceAccountDetailPageReturnType = {
  pageContent?: {
    serviceAccount: ServiceAccount;
    serviceAccountKeys: ServiceAccountKey[];
    groups: { id: string; name: string }[];
  };
  createServiceAccountKey: createServiceAccountKeyFunctionType;
  updateServiceAccount: updateServiceAccountFunctionType;
  disableServiceAccount: disableServiceAccountFunctionType;
  disableServiceAccountKey: disableServiceAccountKeyFunctionType;
};

export const useServiceAccountDetailPage = ({
  serviceAccountId,
  tenantId,
}: {
  serviceAccountId: ServiceAccount["id"];
  tenantId: ServiceAccount["tenantId"];
}): UseServiceAccountDetailPageReturnType => {
  const [shouldRefetch, setShouldRefetch] = React.useState<boolean>(false);

  const { serviceAccountKey, createServiceAccountKey } =
    useServiceAccountKeyCreation({ serviceAccountId, tenantId });
  const {
    isLoading,
    serviceAccount,
    serviceAccountKeys,
    groups,
    updateServiceAccount,
  } = useServiceAccountDetail(serviceAccountId, tenantId, shouldRefetch);

  const createdSAKId = serviceAccountKey?.id;
  React.useEffect(() => {
    if (isLoading) {
      setShouldRefetch(false);
      return;
    }

    if (!serviceAccount) {
      setShouldRefetch(true);
      return;
    }

    if (serviceAccountId !== serviceAccount.id) {
      setShouldRefetch(true);
      return;
    }

    if (
      !!createdSAKId &&
      serviceAccountKeys?.every(({ id }) => id !== createdSAKId)
    ) {
      setShouldRefetch(true);
      return;
    }
  }, [
    setShouldRefetch,
    serviceAccountId,
    isLoading,
    serviceAccount,
    serviceAccountKeys,
    createdSAKId,
  ]);

  const handleDisabled = React.useCallback(() => {
    setShouldRefetch(true);
  }, []);

  const { disableServiceAccount } = useServiceAccountDisable({
    onDisabled: handleDisabled,
  });

  const { disableServiceAccountKey } = useServiceAccountKeyDisable({
    onDisabled: handleDisabled,
  });

  if (!!serviceAccount && !!serviceAccountKeys && !!groups) {
    return {
      pageContent: {
        serviceAccount,
        serviceAccountKeys,
        groups,
      },
      createServiceAccountKey,
      updateServiceAccount,
      disableServiceAccount,
      disableServiceAccountKey,
    };
  }

  return {
    pageContent: undefined,
    createServiceAccountKey,
    updateServiceAccount,
    disableServiceAccount,
    disableServiceAccountKey,
  };
};

const useServiceAccountDetail = (
  serviceAccountId: string,
  tenantId: string,
  shouldRefetch: boolean,
) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [serviceAccount, setServiceAccount] = React.useState<ServiceAccount>();
  const [serviceAccountKeys, setServiceAccountKeys] =
    React.useState<ServiceAccountKey[]>();
  const [groups, setGroups] = React.useState<{ id: string; name: string }[]>();

  React.useEffect(() => {
    const fetchServiceAccountDetail = async () => {
      setIsLoading(true);
      const res = await runGqlQueryV2(() => {
        return gqlClient.serviceAccount(
          { id: serviceAccountId },
          gqlHeader("q"),
        );
      });
      if (res.error) {
        reportUnexpectedError(res.error.rawError);
        alert("サービスアカウントの取得に失敗しました");
        return;
      }
      // URLのパスパラメータから取得したtenantIdと手続きのtenantIdが一致するかどうかをチェックする
      if (res.data!.serviceAccount.tenantId !== tenantId) {
        alert("サービスアカウントの取得に失敗しました");
        return;
      }
      setServiceAccount(res.data!.serviceAccount);
      setServiceAccountKeys(res.data!.serviceAccount.serviceAccountKeys);
      setGroups(res.data!.serviceAccount.belongingGroups);
      setIsLoading(false);
    };
    if (shouldRefetch) {
      fetchServiceAccountDetail();
    }
  }, [
    serviceAccountId,
    setServiceAccount,
    setGroups,
    setIsLoading,
    shouldRefetch,
  ]);

  const updateServiceAccount =
    React.useCallback<updateServiceAccountFunctionType>(
      async (param, callback) => {
        setIsLoading(true);
        const res = await runGqlQueryV2(() => {
          return gqlClient.updateServiceAccount(
            {
              input: {
                id: serviceAccountId,
                description: param.description,
                roles: param.roles,
                groupsRoles: param.groupsRoles,
              },
            },
            gqlHeader("m"),
          );
        });
        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          callback.onFailed("サービスアカウントの更新に失敗しました");
          setIsLoading(false);
          return;
        }

        setServiceAccount(res.data!.updateServiceAccount);
        setGroups(res.data!.updateServiceAccount.belongingGroups);
        callback.onSucceeded(res.data!.updateServiceAccount);
        setIsLoading(false);
      },
      [serviceAccountId, setServiceAccount, setIsLoading],
    );

  return {
    isLoading,
    serviceAccount,
    serviceAccountKeys,
    groups,
    updateServiceAccount,
  };
};

const useServiceAccountKeyCreation = ({
  serviceAccountId,
  tenantId,
}: {
  serviceAccountId: ServiceAccount["id"];
  tenantId: ServiceAccount["tenantId"];
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [serviceAccountKey, setServiceAccountKey] =
    React.useState<ServiceAccountKey>();

  const createServiceAccountKey =
    React.useCallback<createServiceAccountKeyFunctionType>(
      async (param, callback) => {
        setIsLoading(true);
        const res = await runGqlQueryV2(() => {
          return gqlClient.createServiceAccountKey(
            {
              input: {
                serviceAccountId,
                tenantId,
                expiredAtUnixMilliSecond: param.expiredAtUnixMilliSecond,
                name: param.name,
              },
            },
            gqlHeader("m"),
          );
        });
        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          callback.onFailed("サービスアカウントキーの作成に失敗しました");
          setIsLoading(false);
          return;
        }

        const { serviceAccountKey, privateKeyPem } =
          res.data!.createServiceAccountKey;

        setServiceAccountKey(serviceAccountKey);
        callback.onSucceeded(serviceAccountKey, privateKeyPem);
        setIsLoading(false);
      },
      [serviceAccountId, tenantId, setIsLoading, setServiceAccountKey],
    );

  return {
    isLoading,
    serviceAccountKey,
    createServiceAccountKey,
  };
};

const useServiceAccountDisable = ({
  onDisabled,
}: {
  onDisabled: () => void;
}) => {
  const disableServiceAccount =
    React.useCallback<disableServiceAccountFunctionType>(
      async (param, callback) => {
        const res = await runGqlQueryV2(() => {
          return gqlClient.disableServiceAccount(
            {
              id: param.serviceAccountId,
            },
            gqlHeader("m"),
          );
        });
        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          callback.onFailed("サービスアカウントの無効化に失敗しました");
          return;
        }
        callback.onSucceeded();
        onDisabled();
      },
      [onDisabled],
    );

  return {
    disableServiceAccount,
  };
};

const useServiceAccountKeyDisable = ({
  onDisabled,
}: {
  onDisabled: () => void;
}) => {
  const disableServiceAccountKey =
    React.useCallback<disableServiceAccountKeyFunctionType>(
      async (param, callback) => {
        const res = await runGqlQueryV2(() => {
          return gqlClient.disableServiceAccountKey(
            {
              id: param.serviceAccountKeyId,
            },
            gqlHeader("m"),
          );
        });
        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          callback.onFailed("サービスアカウントキーの無効化に失敗しました");
          return;
        }
        callback.onSucceeded();
        onDisabled();
      },
      [onDisabled],
    );

  return {
    disableServiceAccountKey,
  };
};
